import React from 'react';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { mockProjects, mockTasks } from '../data/mock';

export default function DashboardView({ onNavigate }) {
  const activeProjects = mockProjects.filter(p => p.status !== 'completed').length;
  
  const gaugeOption = {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        splitNumber: 10,
        itemStyle: {
          color: '#00f3ff',
          shadowColor: 'rgba(0,243,255,0.5)',
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowOffsetY: 0
        },
        progress: {
          show: true,
          roundCap: true,
          width: 12
        },
        pointer: { show: false },
        axisLine: {
          roundCap: true,
          lineStyle: { width: 12, color: [[1, '#2a2a2a']] }
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        title: { show: false },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, '-10%'],
          fontSize: 40,
          fontWeight: 'bolder',
          formatter: '{value}%',
          color: '#00f3ff'
        },
        data: [{ value: 68 }]
      }
    ]
  };

  return (
    <div className="space-y-6">
      <header className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">Workshop Status</h1>
          <p className="text-gray-400 font-mono text-sm">SYSTEM.ONLINE // ALL_SYSTEMS_NOMINAL</p>
        </div>
        <button 
          onClick={() => onNavigate('projects')}
          className="hidden md:flex items-center gap-2 bg-maker-cyan/10 hover:bg-maker-cyan/20 text-maker-cyan px-4 py-2 rounded-lg border border-maker-cyan/30 transition-all glow-cyan"
        >
          <SafeIcon name="Plus" />
          <span>New Build</span>
        </button>
      </header>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'ACTIVE BUILDS', value: activeProjects, icon: 'Tool', color: 'text-maker-cyan', bg: 'bg-maker-cyan/10' },
          { label: 'MATERIAL VALUE', value: '$1,240', icon: 'DollarSign', color: 'text-maker-tan', bg: 'bg-maker-tanDim' },
          { label: 'NEXT DEADLINE', value: '4 Days', icon: 'Clock', color: 'text-red-400', bg: 'bg-red-400/10' },
        ].map((stat, i) => (
          <div key={i} className="glass-panel p-6 rounded-xl flex items-center justify-between group hover:border-graphite-600 transition-colors">
            <div>
              <p className="text-gray-500 font-mono text-xs mb-1">{stat.label}</p>
              <h3 className="text-3xl font-bold text-white group-hover:text-glow transition-all">{stat.value}</h3>
            </div>
            <div className={`p-3 rounded-lg ${stat.bg} ${stat.color}`}>
              <SafeIcon name={stat.icon} className="w-6 h-6" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Efficiency Gauge */}
        <div className="glass-panel p-6 rounded-xl lg:col-span-1 flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <SafeIcon name="Activity" className="w-24 h-24" />
          </div>
          <h3 className="text-gray-400 font-mono text-sm uppercase self-start w-full border-b border-graphite-700 pb-2 mb-4">Overall Efficiency</h3>
          <div className="w-full h-48 -mt-4">
            <ReactECharts option={gaugeOption} style={{ height: '100%', width: '100%' }} />
          </div>
          <p className="text-center text-sm text-gray-400 mt-2">Projects completed ahead of schedule this month.</p>
        </div>

        {/* Priority Queue */}
        <div className="glass-panel p-6 rounded-xl lg:col-span-2">
           <h3 className="text-gray-400 font-mono text-sm uppercase border-b border-graphite-700 pb-2 mb-4 flex justify-between">
            <span>Priority Queue</span>
            <span className="text-maker-cyan">RUNNING</span>
          </h3>
          <div className="space-y-3">
            {mockTasks.map(task => {
              const project = mockProjects.find(p => p.id === task.projectId);
              return (
                <div key={task.id} className="flex items-center gap-4 p-3 rounded-lg bg-graphite-800/50 border border-graphite-700 hover:border-maker-cyan/30 transition-colors">
                  <button className={`w-5 h-5 rounded flex items-center justify-center border ${task.done ? 'bg-maker-cyan border-maker-cyan text-graphite-900' : 'border-gray-500 text-transparent'}`}>
                    <SafeIcon name="Check" className="w-3 h-3" />
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm ${task.done ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{task.task}</p>
                    <p className="text-xs text-maker-tan font-mono mt-1">{project?.name || 'Unknown'}</p>
                  </div>
                  {!task.done && (
                    <span className="px-2 py-1 text-xs rounded bg-maker-cyan/10 text-maker-cyan border border-maker-cyan/20">
                      PENDING
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  );
}